import { lazy, Suspense } from 'react'
import { createRootRoute, Outlet, useRouterState } from '@tanstack/react-router'

import { AppHeader } from '@/components/app-header'
import { PUBLIC_PATHS } from '@/config/auth-routes'
import { RequireSession } from '@/features/auth/components/require-session'

// Router devtools are loaded lazily and stripped from production builds.
const RouterDevtools = import.meta.env.PROD
  ? () => null
  : lazy(() =>
      import('@tanstack/react-router-devtools').then((module) => ({
        default: module.TanStackRouterDevtools,
      })),
    )

export const Route = createRootRoute({
  component: RootLayout,
})

function RootLayout() {
  // `resolvedLocation`, not `location`: `location` changes as soon as a
  // navigation starts, while `<Outlet/>` goes on rendering the previous match
  // until the new one is ready. Choosing the shell from `location` therefore
  // puts the outgoing protected page inside the public shell for one commit —
  // outside `RequireSession`, long enough for its queries to fire without a
  // session. Reading what the Outlet reads keeps the two in step.
  const pathname = useRouterState({
    select: (state) => (state.resolvedLocation ?? state.location).pathname,
  })

  if (PUBLIC_PATHS.has(pathname)) return <ChromelessLayout />

  if (pathname === '/chat') {
    return (
      <RequireSession>
        <ChromelessLayout />
      </RequireSession>
    )
  }

  return (
    <RequireSession>
      <AppShellLayout />
    </RequireSession>
  )
}

function ChromelessLayout() {
  return (
    <div className="h-full bg-background text-foreground">
      <Outlet />
      <Devtools />
    </div>
  )
}

function AppShellLayout() {
  return (
    <div className="flex h-full flex-col bg-background text-foreground">
      <AppHeader />
      <main className="min-h-0 flex-1">
        <Outlet />
      </main>
      <Devtools />
    </div>
  )
}

function Devtools() {
  return (
    <Suspense fallback={null}>
      <RouterDevtools position="bottom-left" />
    </Suspense>
  )
}
